import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Select, Skeleton } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { ContractItem, MonitoredSiteItem, Paginated } from '@/lib/types';

/**
 * NetDoc-Standorte eines Kunden: Standorte zuordnen und je Standort den Wartungsvertrag wählen.
 * Ohne eigene Zuordnung gehören Standorte zum Kunden ihres NetDoc-Mandanten.
 */
export function CustomerSitesCard({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canEdit = can('devices.manage');
  const [adding, setAdding] = useState('');
  const key = ['monitoring', 'sites', customerId];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api<{ items: MonitoredSiteItem[] }>(`/monitoring/sites?customerId=${customerId}`) });
  const { data: all } = useQuery({ queryKey: ['monitoring', 'sites', 'all'], queryFn: () => api<{ items: MonitoredSiteItem[] }>('/monitoring/sites'), enabled: canEdit });
  const { data: contracts } = useQuery({
    queryKey: ['contracts', 'customer', customerId],
    queryFn: () => api<Paginated<ContractItem>>(`/contracts?customerId=${customerId}&pageSize=50`),
    enabled: can('contracts.read'),
  });
  const service = contracts?.items.filter((c) => c.kind === 'SERVICE' && c.status !== 'TERMINATED' && c.status !== 'EXPIRED') ?? [];
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { customerId?: string | null; contractId?: string | null } }) => api(`/monitoring/sites/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['monitoring', 'sites'] });
      setAdding('');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const mine = data?.items ?? [];
  const candidates = (all?.items ?? []).filter((s) => !mine.some((m) => m.id === s.id));
  const anyContract = mine.some((s) => s.contract);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-sm">
          <MapPin className="size-4 text-muted-foreground" /> RMM-Standorte (NetDoc)
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        {isLoading && <Skeleton className="h-16" />}
        {!isLoading && mine.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Keine Standorte zugeordnet. Standorte werden mit den NetDoc-Endpunkten importiert und gehören automatisch zum Kunden ihres NetDoc-Mandanten bzw. zum Kunden, dessen Kundennummer am Standort als <code>external_id</code> steht.
          </p>
        )}
        {mine.map((s) => (
          <div key={s.id} className="grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_16rem_auto] sm:items-center">
            <div className="min-w-0">
              <p className="truncate font-medium">
                {s.name} {s.code && <span className="text-xs text-muted-foreground">({s.code})</span>}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {s.address ? `${s.address.replace(/\s*\n\s*/g, ', ')} · ` : ''}
                {s.hostCount} Endpunkt{s.hostCount === 1 ? '' : 'e'} · {s.customer ? 'direkt zugeordnet' : `über NetDoc-Mandant „${s.group?.name ?? '–'}“`}
              </p>
            </div>
            {canEdit ? (
              <Select
                aria-label={`Wartungsvertrag für ${s.name}`}
                value={s.contract?.id ?? ''}
                onChange={(e) => update.mutate({ id: s.id, body: { contractId: e.target.value || null } })}
              >
                <option value="">– kein Wartungsvertrag –</option>
                {service.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.number} · {c.title}
                    {c.effective ? '' : ' (nicht aktiv)'}
                  </option>
                ))}
              </Select>
            ) : (
              <span className="text-sm">{s.contract ? `${s.contract.number} · ${s.contract.title}` : <Badge variant="outline">ohne Wartungsvertrag</Badge>}</span>
            )}
            {canEdit && s.customer && (
              <Button type="button" variant="ghost" size="icon" className="size-8 justify-self-end" aria-label={`Zuordnung von ${s.name} lösen`} onClick={() => update.mutate({ id: s.id, body: { customerId: null } })}>
                <X />
              </Button>
            )}
          </div>
        ))}
        {anyContract && (
          <Alert>
            Verträge gelten hier je Standort: Monitoring-Tickets mit der Bedingung „Nur mit Wartungsvertrag“ entstehen nur für Geräte an Standorten mit aktivem Vertrag.
          </Alert>
        )}
        {canEdit && candidates.length > 0 && (
          <div className="flex gap-2">
            <Select aria-label="Standort zuordnen" value={adding} onChange={(e) => setAdding(e.target.value)}>
              <option value="">Standort aus NetDoc zuordnen …</option>
              {candidates.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.code ? ` (${s.code})` : ''} – {s.customer?.name ?? s.group?.customer?.name ?? s.group?.name ?? 'ohne Kunde'}
                </option>
              ))}
            </Select>
            <Button type="button" variant="outline" disabled={!adding} loading={update.isPending} onClick={() => update.mutate({ id: adding, body: { customerId } })}>
              <Plus /> Zuordnen
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
